import { CommonModule } from '@angular/common';
import { 
  Component, Input, Output, EventEmitter, 
  signal, computed, effect, inject, 
  OnInit, Renderer2, ElementRef, ViewChild, AfterViewInit 
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigSelected } from '../../interfaces/interfaces';

@Component({
  selector: 'app-selected',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './selected.component.html',
  styleUrl: './selected.component.css',
})
export class SelectedComponent implements OnInit, AfterViewInit {
  private fb = inject(FormBuilder);
  private renderer = inject(Renderer2);

  @Input({ required: true })
  set config(value: any) {
    this.configSignal.set({
      label: value.label || 'Seleccione',
      placeholder: value.placeholder || 'Seleccione una opción',
      defaultValue: value.defaultValue ?? '',
      enabled: value.enabled ?? true,
      options: value.options || [],
      validation: value.validation || null,
      formName: value.formName || null,
      textError: value.textError,
    });
  }

  @Input() timeoutSeconds: number = 8;

  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;

  configSignal = signal<any>({
    label: 'Seleccione',
    placeholder: 'Seleccione una opción',
    defaultValue: '',
    enabled: true,
    options: [],
    validation: null,
    formName: null,
  });

  enabled = computed(() => this.configSignal().enabled);
  currentValue = computed(() => this.configSignal().defaultValue);

  form: FormGroup;
  hasWritten = signal(false);
  offTimeOut = signal(false);
  private errorDebounceTimer: any;
  private viewInitialized = signal(false);

  @Output() selectValue = new EventEmitter<any>();

  private debounceTimer: any;

  constructor() {
    this.form = this.fb.group({
      selected: [
        { 
          value: this.currentValue(), 
          disabled: !this.enabled()
        },
        this.configSignal().validation 
          ? [Validators.required, this.configSignal().validation] 
          : [Validators.required],
      ],
    });

    effect(() => {
      const control = this.form.get('selected');
      this.enabled() ? control?.enable() : control?.disable();
      
      // Solo aplicar estilos si la vista ya está inicializada
      if (this.enabled()) {
        this.getStyleEnabled();
      } else {
        this.getStyleTextDisabled();
      }
    });

    effect(() => {
      const control = this.form.get('selected');
      const validations = this.configSignal().validation;
      control?.setValidators(Validators.compose(validations ?? []));
      control?.updateValueAndValidity();
    });

    // Reemplaza el effect que inicia el countdown automáticamente
    effect(() => {
      if (this.currentValue() !== null) {
        this.form.get('selected')?.setValue(this.currentValue());
        // Solo iniciar countdown si no hay valor por defecto
        if (this.currentValue() === null || this.currentValue() === '') {
          this.startCountdown().then((timeoutDone) => {
            if (timeoutDone && (this.form.value.selected === null || this.form.value.selected === '')) {
              this.offTimeOut.set(true);
              this.getStyleError();
            }
          });
        }
      }
    });

    // Escuchar cambios del valor y aplicar la misma lógica de estilos
    this.form.get('selected')?.valueChanges.subscribe(() => {
      const control = this.form.get('selected');
      if (!control) return;

      if (this.errorDebounceTimer) {
        clearTimeout(this.errorDebounceTimer);
      }

      if (control.value === '' && control.invalid && this.hasWritten()) {
        this.getStyleError();
        return;
      }

      if (control.valid && this.enabled()) {
        this.getStyleEnabled();
        return;
      }

      setTimeout(() => {
        // Solo si el campo sigue vacío o inválido y no se ha escrito nada
        const control = this.form.get('selected');
        if (
          control &&
          (control.value === '' || control.invalid) &&
          !this.hasWritten()
        ) {
          this.offTimeOut.set(true);
          this.getStyleError();
        }
      }, this.timeoutSeconds * 1000);

      this.errorDebounceTimer = setTimeout(() => {
        if (
          control.invalid && (control.touched || control.dirty) ||
          this.offTimeOut() ||
          (control.value === '' && control.invalid && this.hasWritten())
        ) {
          this.getStyleError();
        } else if (this.enabled()) {
          this.getStyleEnabled();
        } else {
          this.getStyleTextDisabled();
        }
      }, 1700);
    });

    this.form.valueChanges.subscribe(() => {
      const control = this.form.get('selected');
      const valid = control?.valid;
      if (valid) {
        this.offTimeOut.set(false);
      }
    })

    this.form.get('selected')?.valueChanges.subscribe(() => {
      this.hasWritten.set(true);
      this.resetDebounce();
    });
  }

  hasError = computed(() => {
      const control = this.selectedControl();
      if (!control) return false;

      const isTimedOut = this.offTimeOut();
      
      return isTimedOut;
    });

  selectedControl = computed(() => this.form.get('selected'));

  ngOnInit() {}

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    this.applyInitialStyles();
  }

  private applyInitialStyles() {
    if (this.enabled()) {
      this.getStyleEnabled();
    } else {
      this.getStyleTextDisabled();
    }
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized()) {
      callback();
    }
  }

  onBlurSelect() {
    this.emitIfValid();
  }

  resetDebounce() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.emitIfValid();
    }, 100);
  }

  emitIfValid() {
    if (this.form.valid) {
      if(typeof(this.configSignal()?.options().at(0)?.value) === 'boolean'){
        this.selectValue.emit({value: JSON.parse(this.form.value.selected), formName: this.configSignal().formName});
      }else{
        this.selectValue.emit({value: this.form.value.selected, formName: this.configSignal().formName});
      }
    }
  }

  startCountdown(): Promise<boolean> {
    return new Promise((resolve) => {
      let counter = this.timeoutSeconds;
      function tick() {
        if (counter === 0) {
          resolve(true);
        } else {
          counter--;
          setTimeout(tick, 1000);
        }
      }
      tick();
    });
  }

  getStyleError(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.addClass(this.inbox.nativeElement, 'border-error')
      }
    });
  }
  
  getStyleEnabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.addClass(this.inbox.nativeElement, 'border-enabled')
      }
    });
  }

  getStyleTextDisabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.inbox.nativeElement, 'border-disabled')
      }
    });
  }

  // Método para limpiar recursos
  ngOnDestroy() {
    if (this.errorDebounceTimer) {
      clearTimeout(this.errorDebounceTimer);
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}